import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { fetchClickerApps } from './clicker-apps-shared';
import { FARM_LOCATION_OPTIONS } from '../core/farm-locations';
import { createAppFilterState, buildAppFilter, applyAppFilter, stampAppFilterData, } from './clicker-apps-filter';
// The capability pair as one picker: an app must support at least one task, so the three
// valid combinations are the three options — a state the old checkbox pair could not hold
// (both off) is simply not offered.
const CAPABILITY_OPTIONS = ['both', 'deposit', 'payout'];
/** <option> list for the country picker — the farm location list, "Not set" included. */
function countryOptions(selected) {
    return FARM_LOCATION_OPTIONS.map((loc) => `<option value="${esc(loc.code)}"${loc.code === selected ? ' selected' : ''}>${esc(loc.flag ? `${loc.flag} ${loc.label}` : loc.label)}</option>`).join('');
}
/**
 * "Apps" clicker-settings tab — the master clicker app catalog (superadmin-only,
 * gated by `can('clicker.apps')` at the call site in clicker-settings-view.ts).
 * Lists every app with deposit/payout capability toggles, lets a superadmin add a
 * new app (the name must match a duo/ flow) and delete one. Row edits are explicit:
 * the row's Save button enables once something changed and persists the whole row via
 * POST /api/clicker-apps; deletion via DELETE /api/clicker-apps/:name.
 */
export async function renderClickerAppsTab(panel) {
    panel.innerHTML = `
        <div class="settings-section">
            <h3 class="settings-section__title">${t('clickertab.add_app')}</h3>
            <form class="settings-app-form" novalidate>
                <div class="modal__field">
                    <label>${t('clickertab.app_name')}</label>
                    <input class="settings-input ca-name" type="text" autocomplete="off" maxlength="40" placeholder="${t('clickertab.app_name_placeholder')}" title="${t('clickertab.app_name_hint')}" required />
                </div>
                <div class="modal__field">
                    <label>${t('clickertab.capability')}</label>
                    <select class="settings-input ca-caps">
                        ${CAPABILITY_OPTIONS.map((c) => `<option value="${c}">${t(`clickertab.cap_${c}`)}</option>`).join('')}
                    </select>
                </div>
                <div class="modal__field">
                    <label>${t('clickertab.country')}</label>
                    <select class="settings-input ca-country">${countryOptions('')}</select>
                </div>
                <button type="submit" class="modal__submit">${t('clickertab.add_app')}</button>
            </form>
            <div class="settings-error modal__error ca-add-error" style="display:none"></div>
        </div>
        <div class="settings-section">
            <h3 class="settings-section__title">${t('clickertab.apps')}</h3>
            <div class="farm-filter ca-filter"></div>
            <div class="ca-list">${t('common.loading')}</div>
            <p class="settings-hint ca-empty" style="display:none">${t('clickertab.no_apps_match')}</p>
        </div>
    `;
    const form = panel.querySelector('.settings-app-form');
    const nameInput = panel.querySelector('.ca-name');
    const capsSel = panel.querySelector('.ca-caps');
    const countrySel = panel.querySelector('.ca-country');
    const addError = panel.querySelector('.ca-add-error');
    const listEl = panel.querySelector('.ca-list');
    const filterEl = panel.querySelector('.ca-filter');
    const emptyEl = panel.querySelector('.ca-empty');
    const appFilter = createAppFilterState();
    const showAddErr = (msg) => { addError.textContent = msg; addError.style.display = 'block'; };
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        addError.style.display = 'none';
        const name = nameInput.value.trim();
        if (!name) {
            showAddErr(t('clickertab.err_app_name_required'));
            return;
        }
        const btn = form.querySelector('.modal__submit');
        btn.disabled = true;
        try {
            const r = await fetch('/api/clicker-apps', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name, supports_deposit: capsSel.value !== 'payout', supports_payout: capsSel.value !== 'deposit',
                    country: countrySel.value,
                }),
            });
            if (!r.ok) {
                showAddErr((await r.json()).error ?? t('clickertab.err_failed_add_app'));
                return;
            }
            form.reset();
            void loadList();
        }
        catch {
            showAddErr(t('clickertab.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
    // Persist an edit to an existing app. The endpoint is an upsert of the WHOLE row, so
    // every field must be sent even when only one changed.
    async function saveApp(app, deposit, payout, country) {
        if (!deposit && !payout)
            return false; // would orphan the app; the UI re-checks instead
        const r = await fetch('/api/clicker-apps', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: app.name, supports_deposit: deposit, supports_payout: payout,
                sort_order: app.sort_order, country,
            }),
        });
        return r.ok;
    }
    async function loadList() {
        let apps;
        emptyEl.style.display = 'none';
        try {
            apps = await fetchClickerApps();
        }
        catch {
            filterEl.innerHTML = '';
            listEl.innerHTML = `<p class="settings-hint">${t('clickertab.failed_to_load_apps')}</p>`;
            return;
        }
        if (apps.length === 0) {
            filterEl.innerHTML = '';
            listEl.innerHTML = `<p class="settings-hint">${t('clickertab.no_apps')}</p>`;
            return;
        }
        const rows = apps.map((a) => `
            <tr data-name="${esc(a.name)}">
                <td>${esc(a.name)}</td>
                <td style="text-align:center"><input type="checkbox" class="ca-row-deposit" ${a.supports_deposit ? 'checked' : ''} /></td>
                <td style="text-align:center"><input type="checkbox" class="ca-row-payout" ${a.supports_payout ? 'checked' : ''} /></td>
                <td><select class="settings-input ca-row-country">${countryOptions(a.country ?? '')}</select></td>
                <td class="ca-actions">
                    <button type="button" class="modal__submit ca-save" disabled>${t('common.save')}</button>
                    <button type="button" class="modal__submit modal__submit--danger ca-del" data-name="${esc(a.name)}">${t('common.delete')}</button>
                </td>
            </tr>
        `).join('');
        listEl.innerHTML = `
            <table class="access-matrix">
                <thead><tr><th>${t('clickertab.app')}</th><th>${t('clickertab.deposit')}</th><th>${t('clickertab.payout')}</th><th>${t('clickertab.country')}</th><th></th></tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <div class="settings-error modal__error ca-row-error" style="display:none"></div>
        `;
        const applyFilter = () => {
            emptyEl.style.display = applyAppFilter(listEl, appFilter) ? 'none' : 'block';
        };
        listEl.querySelectorAll('tr[data-name]').forEach((tr) => {
            stampAppFilterData(tr, apps.find((x) => x.name === tr.dataset['name']));
        });
        buildAppFilter(filterEl, appFilter, apps, applyFilter);
        applyFilter();
        const rowError = listEl.querySelector('.ca-row-error');
        const showRowErr = (msg) => { rowError.textContent = msg; rowError.style.display = 'block'; };
        listEl.querySelectorAll('tr[data-name]').forEach((tr) => {
            const name = tr.dataset['name'];
            const app = apps.find((x) => x.name === name);
            const dep = tr.querySelector('.ca-row-deposit');
            const pay = tr.querySelector('.ca-row-payout');
            const country = tr.querySelector('.ca-row-country');
            const saveBtn = tr.querySelector('.ca-save');
            // Nothing is written until Save is pressed. The button only enables while the
            // row differs from what the server holds.
            const isDirty = () => dep.checked !== app.supports_deposit
                || pay.checked !== app.supports_payout
                || country.value !== (app.country ?? '');
            const refreshSave = () => { saveBtn.disabled = !isDirty(); };
            const onSave = async () => {
                rowError.style.display = 'none';
                if (!dep.checked && !pay.checked) {
                    showRowErr(`${app.name}: ${t('clickertab.err_select_capability_row')}`);
                    return;
                }
                saveBtn.disabled = true;
                const ok = await saveApp(app, dep.checked, pay.checked, country.value);
                if (ok) {
                    app.supports_deposit = dep.checked;
                    app.supports_payout = pay.checked;
                    app.country = country.value;
                    stampAppFilterData(tr, app);
                    applyFilter();
                    saveBtn.textContent = t('common.saved');
                    setTimeout(() => { saveBtn.textContent = t('common.save'); }, 1500);
                }
                else {
                    showRowErr(`${app.name}: ${t('clickertab.err_row_failed_to_save')}`);
                }
                refreshSave();
            };
            dep.addEventListener('change', refreshSave);
            pay.addEventListener('change', refreshSave);
            country.addEventListener('change', refreshSave);
            saveBtn.addEventListener('click', () => void onSave());
        });
        listEl.querySelectorAll('.ca-del').forEach((btn) => {
            let armed = false;
            btn.addEventListener('click', async () => {
                if (!armed) {
                    armed = true;
                    btn.textContent = t('common.confirm');
                    setTimeout(() => { armed = false; btn.textContent = t('common.delete'); }, 4000);
                    return;
                }
                btn.disabled = true;
                try {
                    const name = btn.dataset['name'] ?? '';
                    const r = await fetch(`/api/clicker-apps/${encodeURIComponent(name)}`, { method: 'DELETE' });
                    if (!r.ok) {
                        btn.disabled = false;
                        armed = false;
                        btn.textContent = t('common.delete');
                        return;
                    }
                    void loadList();
                }
                catch {
                    btn.disabled = false;
                }
            });
        });
    }
    void loadList();
}
